import React from 'react';
import { BlogPost } from '../types';
import { ArrowRight, Calendar } from 'lucide-react';

interface BlogPreviewProps {
  posts: BlogPost[];
  onNavigate: (page: 'blog', sectionId?: string) => void;
  onReadPost: (post: BlogPost) => void;
}

const BlogPreview: React.FC<BlogPreviewProps> = ({ posts, onNavigate, onReadPost }) => {
  // Show only up to 3 posts on the homepage
  const recentPosts = posts.slice(0, 3);

  return (
    <section className="py-24 bg-gray-50 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12">
          <div>
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-orange-100 border border-orange-200 mb-4">
              <span className="text-orange-800 text-xs font-bold uppercase tracking-widest">
                Blogs & Insights
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-black tracking-tight">
              Latest from the <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-700">Lab</span>
            </h2>
          </div>
          <button
            onClick={() => onNavigate('blog')}
            className="hidden md:inline-flex items-center text-orange-800 font-bold hover:text-orange-900 transition-colors"
          >
            View all articles <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {recentPosts.map((post) => (
            <article 
              key={post.id}
              className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group cursor-pointer flex flex-col"
              onClick={() => onReadPost(post)}
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={post.imageUrl} 
                  alt={post.title} 
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                  {post.category}
                </div>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center text-xs text-gray-500 mb-3">
                  <Calendar className="w-3 h-3 mr-1" />
                  {post.date}
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors line-clamp-2">
                  {post.title}
                </h3>
                
                <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2">
                  {post.excerpt}
                </p>
                
                <div className="mt-auto flex items-center text-orange-800 font-bold text-sm">
                  Read Article <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-8 text-center md:hidden">
          <button
            onClick={() => onNavigate('blog')}
            className="inline-flex items-center text-orange-800 font-bold hover:text-orange-900 transition-colors"
          >
            View all articles <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;
